(function (root) {
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  const variant = line => String(line.variant_id ?? line.id);
  const plain = line => !Object.keys(line.properties || {}).length && !line.selling_plan_allocation && !line.selling_plan;
  const uncertain = code => Object.assign(new Error(code), { uncertain: true });
  const canonical = value => JSON.stringify(value, Object.keys(value).sort());
  const shape = cart => {
    const counts = new Map();
    for (const line of cart.items) {
      const key = JSON.stringify([variant(line), canonical(line.properties || {}), String(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || '')]);
      counts.set(key, (counts.get(key) || 0) + line.quantity);
    }
    return JSON.stringify([...counts].filter(([, count]) => count).sort());
  };
  const clone = value => JSON.parse(JSON.stringify(value));
  // The Liquid renderer always emits stock, using explicit null for untracked
  // inventory or a continue-selling policy. Missing is not equivalent to null.
  const merchandiseValid = (item, sample = false) => {
    if (!item || typeof item !== 'object' || Array.isArray(item) ||
        typeof item.available !== 'boolean' || !integer(item.price) ||
        !(item.stock === null || integer(item.stock))) return false;
    if (sample) return typeof item.id === 'string' && /^\d+$/.test(item.id);
    const rule = item.rule;
    return !!rule && typeof rule === 'object' && !Array.isArray(rule) &&
      integer(rule.min) && rule.min > 0 && integer(rule.increment) && rule.increment > 0 &&
      (rule.max === null || (integer(rule.max) && rule.max >= rule.min));
  };
  async function digest(value) {
    const bytes = await root.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
  }
  function createShopifyIO() {
    const request = async (path, body) => {
      const response = await root.fetch((root.Shopify?.routes?.root || '/') + path, {
        credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
        ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : {})
      });
      const data = await response.json();
      if (!response.ok) throw Object.assign(new Error(data.description || 'CART_REQUEST_FAILED'), { rejected: response.status >= 400 && response.status < 500 });
      return data;
    };
    return { read: () => request('cart.js'), add: items => request('cart/add.js', { items }), change: data => request('cart/change.js', data) };
  }
  function create({ campaign, coordinator, io = createShopifyIO(), hash = digest, readSelection, validateExistingOffer = false, now = Date.now, uuid = () => root.crypto.randomUUID(), enabled = false }) {
    const config = campaign.config;
    const sampleId = String(config.sampleVariantId);
    const properties = { _tm_bb_campaign: config.id, _tm_bb_revision: config.revision };
    const owned = line => variant(line) === sampleId && !line.selling_plan_allocation && !line.selling_plan && canonical(line.properties || {}) === canonical(properties);
    const qty = lines => lines.reduce((sum, line) => sum + line.quantity, 0);
    const samples = cart => cart.items.filter(line => variant(line) === sampleId);
    const identity = async cart => {
      if (typeof cart.token !== 'string' || !cart.token) throw uncertain('CART_IDENTITY_UNAVAILABLE');
      return hash(cart.token);
    };
    const read = async () => {
      try {
      const cart = await io.read();
      campaign.evaluate(cart, now());
      if (!integer(cart.total_price) || cart.items.some(line => !integer(line.final_line_price) || !integer(line.line_price) || typeof line.key !== 'string' || !line.key)) throw uncertain('INVALID_CART_RESPONSE');
      await identity(cart);
      return cart;
      } catch (error) { throw Object.assign(error, { uncertain: true }); }
    };
    const enabledCheck = () => { if (!enabled) throw new Error('CAMPAIGN_TRANSFER_DISABLED'); };
    const activeWrite = guard => {
      try { return campaign.active(now()) && guard() === true; }
      catch (_) { return false; }
    };
    const requireActiveWrite = guard => {
      if (!activeWrite(guard)) throw new Error('SCOPE_EXPIRED');
    };
    const linesCheck = lines => {
      if (!Array.isArray(lines) || !lines.length || lines.length > 100 ||
          new Set(lines.map(line => String(line.id))).size !== lines.length ||
          lines.some(line => !/^\d+$/.test(String(line.id)) || !integer(line.quantity) || line.quantity < 1 || !campaign.qualifies(line) || !plain(line))) throw new Error('INVALID_SELECTION');
    };
    const projected = (cart, lines) => {
      const next = clone(cart);
      for (const line of lines) {
        const existing = next.items.find(item => variant(item) === String(line.id) && plain(item));
        if (existing) existing.quantity += line.quantity;
        else next.items.push({ variant_id: String(line.id), quantity: line.quantity, price: 0, properties: {} });
      }
      return next;
    };
    // The connected Q1 guest cart exposes FIRSTORDER10 on the cart, while its
    // final line amount remains undiscounted. Recognize only that exact,
    // fully allocated one-bottle shape; all other cart discounts remain unknown.
    const knownFirstOrderDiscount = cart => {
      if (cart.items.length !== 1 || !Array.isArray(cart.cart_level_discount_applications) ||
          cart.cart_level_discount_applications.length !== 1 || !Array.isArray(cart.discount_codes) ||
          cart.discount_codes.length !== 1) return null;
      const line = cart.items[0], application = cart.cart_level_discount_applications[0], code = cart.discount_codes[0];
      const original = (line.original_price ?? line.price) * line.quantity;
      const amount = Math.round(original / 10);
      if (!campaign.qualifies(line) || !plain(line) || line.quantity !== 1 || config.floorBps?.[variant(line)] ||
          !integer(original) || !integer(amount) || line.price !== line.original_price ||
          line.original_line_price !== original || line.final_line_price !== original ||
          line.final_price !== line.original_price || line.line_price !== original - amount ||
          line.total_discount !== amount || line.discount_allocations != null ||
          line.line_level_total_discount !== 0 || !Array.isArray(line.line_level_discount_allocations) ||
          line.line_level_discount_allocations.length !== 0 || !Array.isArray(line.discounts) ||
          line.discounts.length !== 1 || line.discounts[0].title !== 'FIRSTORDER10' ||
          line.discounts[0].amount !== amount || application.type !== 'discount_code' ||
          application.title !== 'FIRSTORDER10' || application.value_type !== 'percentage' ||
          Number(application.value) !== 10 || application.allocation_method !== 'across' ||
          application.target_selection !== 'all' || application.target_type !== 'line_item' ||
          application.total_allocated_amount !== amount || code.code !== 'FIRSTORDER10' ||
          code.applicable !== true || cart.items_subtotal_price !== original ||
          cart.original_total_price !== original || cart.total_discount !== amount ||
          cart.total_price !== original - amount || moneyState(cart).mismatches.length) return null;
      return amount;
    };
    const fingerprint = cart => hash(shape(cart));
    const reviewFingerprint = cart => hash(JSON.stringify({ ...cart, token: undefined,
      items: cart.items.map(({ key, ...line }) => line) }));
    const review = async (cart, lines, unitPrices, includeSamples = true) => {
      if (validateExistingOffer) {
        const mismatches = check(cart, qty(cart.items.filter(owned))).filter(code => ['LINE_ALLOCATION_MISMATCH', 'CART_ALLOCATION_UNRESOLVED', 'SAMPLE_PRICE_MISMATCH', 'FRAGRANCE_DISCOUNT_MISMATCH'].includes(code));
        if (mismatches.length) throw Object.assign(uncertain('ACTUAL_CART_REVIEW_REQUIRED'), { cart, mismatches });
      }
      const lineTotal = cart.items.reduce((sum, line) => sum + line.final_line_price, 0);
      const firstOrderDiscount = cart.total_price === lineTotal ? 0 : knownFirstOrderDiscount(cart);
      if (firstOrderDiscount === null)
        throw Object.assign(uncertain('FULL_BAG_ESTIMATE_UNAVAILABLE'), { cart });
      let merchandise = null;
      if (readSelection) {
        merchandise = await readSelection(lines, config.currency);
        for (const line of lines) {
          const item = merchandise[String(line.id)];
          if (!merchandiseValid(item)) throw new Error('INVALID_SELECTION_DATA');
          const quantity = line.quantity + qty(cart.items.filter(item => variant(item) === String(line.id)));
          const rule = item.rule;
          if (!item.available || (item.stock !== null && quantity > item.stock) ||
              quantity < rule.min || quantity % rule.increment ||
              (rule.max !== null && quantity > rule.max)) throw new Error('SELECTION_UNAVAILABLE');
        }
        if (validateExistingOffer && !merchandiseValid(merchandise._sample, true)) throw new Error('INVALID_SAMPLE_DATA');
        unitPrices = Object.fromEntries(lines.map(line => [String(line.id), merchandise[String(line.id)].price]));
      }
      if (!unitPrices || lines.some(line => !integer(unitPrices[String(line.id)]))) throw new Error('MISSING_REVIEWED_PRICES');
      const projection = projected(cart, lines);
      for (const line of lines) {
        const match = projection.items.find(item => variant(item) === String(line.id) && plain(item));
        const existing = cart.items.filter(item => variant(item) === String(line.id) && plain(item));
        if (existing.some(item => (item.original_price ?? item.price) !== unitPrices[String(line.id)]))
          throw new Error('MERCHANDISE_PRICE_CHANGED');
        if (!existing.length) match.price = unitPrices[String(line.id)];
      }
      const evaluation = campaign.evaluate(projection, now());
      const current = campaign.evaluate(cart, now());
      if (cart.items.some((line, index) => campaign.qualifies(line) &&
          line.final_line_price !== current.lines[index].original - current.lines[index].discount))
        throw Object.assign(uncertain('FULL_BAG_ESTIMATE_UNAVAILABLE'), { cart });
      const selectionSubtotal = lines.reduce((sum, line) => sum + unitPrices[String(line.id)] * line.quantity, 0);
      const currentEligibleDiscount = cart.items.reduce((sum, line, index) => sum + (campaign.qualifies(line) ? current.lines[index].discount : 0), 0) + firstOrderDiscount;
      const projectedEligibleDiscount = projection.items.reduce((sum, line, index) => sum + (campaign.qualifies(line) ? evaluation.lines[index].discount : 0), 0);
      const existingUplift = cart.items.reduce((sum, line, index) => {
        if (!campaign.qualifies(line)) return sum;
        const original = (line.original_price ?? line.price) * line.quantity;
        return sum + Math.round(original * evaluation.lines[index].bps / 10000) - current.lines[index].discount -
          (firstOrderDiscount && index === 0 ? firstOrderDiscount : 0);
      }, 0);
      const selectionDiscount = projectedEligibleDiscount - currentEligibleDiscount - existingUplift;
      if (![selectionSubtotal, currentEligibleDiscount, projectedEligibleDiscount, existingUplift, selectionDiscount].every(integer))
        throw uncertain('FULL_BAG_ESTIMATE_UNAVAILABLE');
      const existingSamples = qty(samples(cart));
      const add = includeSamples ? Math.max(0, evaluation.sampleCap - existingSamples) : 0;
      if (firstOrderDiscount && (projection.items.some(line => !campaign.qualifies(line) || !plain(line) || config.floorBps?.[variant(line)]) ||
          projectedEligibleDiscount <= Math.round((evaluation.subtotal + add * 10) / 10)))
        throw uncertain('FULL_BAG_ESTIMATE_UNAVAILABLE');
      if (validateExistingOffer && add) {
        const sample = merchandise?._sample;
        if (!sample || sample.id !== sampleId || sample.available !== true || sample.price !== 10 ||
            (sample.stock !== null && existingSamples + add > sample.stock)) throw new Error('SAMPLE_UNAVAILABLE_OR_PRICE_CHANGED');
      }
      const incrementalBagCharge = selectionSubtotal - (projectedEligibleDiscount - currentEligibleDiscount) + add * 10;
      const estimatedBagTotal = cart.total_price + incrementalBagCharge;
      if (!Number.isSafeInteger(incrementalBagCharge) || !integer(estimatedBagTotal)) throw uncertain('FULL_BAG_ESTIMATE_UNAVAILABLE');
      return { status: 'estimated', revision: config.revision, campaignId: config.id, currency: config.currency,
        cartIdentity: await identity(cart), cartFingerprint: await fingerprint(cart), cartReviewFingerprint: await reviewFingerprint(cart),
        merchandise, includeSamples, expiresAt: config.endsAt, eligibleCount: evaluation.eligibleCount, selection: lines.map(({ id, quantity }) => ({ id: String(id), quantity })),
        unitPrices: Object.fromEntries(lines.map(line => [String(line.id), unitPrices[String(line.id)]])),
        sampleQuantityToAdd: add, existingSampleQuantity: existingSamples, sampleUnitCharge: 10,
        additionalSampleCharge: add * 10, sampleCap: evaluation.sampleCap,
        selectionSubtotal, selectionDiscount, existingUplift, cartTotalBefore: cart.total_price,
        incrementalBagCharge, estimatedBagTotal };
    };
    const result = (cart, mismatches, paidConfirmed = true) => ({ mode: mismatches.length ? 'cart-review-required' : 'cart-reconciled',
      confirmed: paidConfirmed, offersReconciled: !mismatches.length, cart, mismatches });
    const moneyState = cart => {
      const mismatches = [], nets = [];
      const sumAmounts = allocations => {
        if (!Array.isArray(allocations) || allocations.some(value => !integer(value.amount))) return null;
        const total = allocations.reduce((sum, value) => sum + value.amount, 0);
        return integer(total) ? total : null;
      };
      const applications = cart.cart_level_discount_applications ?? [];
      const order = Array.isArray(applications) && applications.every(value => integer(value.total_allocated_amount))
        ? applications.filter(value => value.target_type !== 'shipping_line').reduce((sum, value) => sum + value.total_allocated_amount, 0) : null;
      let originalTotal = 0, finalTotal = 0;
      for (const line of cart.items) {
        const original = (line.original_price ?? line.price) * line.quantity;
        const final = line.final_line_price;
        originalTotal += original; finalTotal += final;
        const reduction = original - final;
        if (!integer(original) || !integer(final) || final > original ||
            (line.original_line_price != null && line.original_line_price !== original) ||
            (line.final_price != null && (!integer(line.final_price) || line.final_price * line.quantity !== final)) ||
            (line.line_level_total_discount != null && line.line_level_total_discount !== reduction) ||
            (line.line_level_discount_allocations != null && sumAmounts(line.line_level_discount_allocations) !== reduction)) mismatches.push('LINE_ALLOCATION_MISMATCH');
        const allocations = line.discount_allocations ?? (order ? line.discounts : undefined);
        if (allocations != null) {
          const all = sumAmounts(allocations);
          if (all === null || all < reduction || all > original) { nets.push(null); mismatches.push('LINE_ALLOCATION_MISMATCH'); }
          else nets.push(original - all);
        } else nets.push(order === 0 ? final : null);
      }
      if (!integer(originalTotal) || !integer(finalTotal) || !integer(order) || finalTotal - order !== cart.total_price ||
          (cart.items_subtotal_price != null && cart.items_subtotal_price !== finalTotal) ||
          (cart.original_total_price != null && cart.original_total_price !== originalTotal) ||
          (cart.total_discount != null && cart.total_discount !== originalTotal - cart.total_price)) mismatches.push('CART_ALLOCATION_UNRESOLVED');
      if (nets.every(value => value !== null) && nets.reduce((sum, value) => sum + value, 0) !== cart.total_price) mismatches.push('CART_ALLOCATION_UNRESOLVED');
      return { mismatches, nets };
    };
    const check = (cart, target) => {
      const evaluation = campaign.evaluate(cart, now());
      const monetary = moneyState(cart);
      const mismatches = [...monetary.mismatches];
      if (!evaluation.active) mismatches.push('CAMPAIGN_EXPIRED');
      if (qty(samples(cart)) > evaluation.sampleCap || qty(cart.items.filter(owned)) !== target) mismatches.push('SAMPLE_QUANTITY_MISMATCH');
      for (let index = 0; index < cart.items.length; index++) {
        const line = cart.items[index], expected = evaluation.lines[index];
        if (variant(line) === sampleId && monetary.nets[index] !== line.quantity * 10) mismatches.push('SAMPLE_PRICE_MISMATCH');
        if (campaign.qualifies(line) && line.final_line_price !== expected.original - expected.discount) mismatches.push('FRAGRANCE_DISCOUNT_MISMATCH');
      }
      return [...new Set(mismatches)];
    };
    const finish = (operation, cart, mismatches, paidConfirmed = true) => {
      if (!mismatches.length) coordinator.clear(operation.operationId);
      return { ...result(cart, mismatches, paidConfirmed && operation.kind !== 'reduce'), uncertain: !!mismatches.length };
    };
    const reviewedTotalMismatches = (operation, cart, expectedField) => operation.kind === 'transfer' &&
      (!integer(operation[expectedField]) || cart.total_price !== operation[expectedField]) ? ['REVIEWED_TOTAL_CHANGED'] : [];
    async function continueOperation(operation, cart, allowSampleWrite = true, guard = () => true) {
      if (!['paid-uncertain', 'paid-confirmed', 'sample-uncertain'].includes(operation.phase) ||
          (operation.phase !== 'sample-uncertain' && (!integer(operation.disclosedSamples) || operation.disclosedSamples > 4)) ||
          (operation.phase === 'sample-uncertain' && (!integer(operation.sampleTarget) || operation.sampleTarget > 4))) throw uncertain('INVALID_RECOVERY_PHASE');
      if (operation.revision !== config.revision || operation.campaignId !== config.id || operation.cartIdentity !== await identity(cart)) throw uncertain('CART_OR_CAMPAIGN_CHANGED');
      if (operation.phase === 'paid-uncertain') {
        if (await fingerprint(cart) !== operation.expectedPaid) throw uncertain('PAID_RESULT_UNCERTAIN');
        operation.phase = 'paid-confirmed';
        coordinator.save(operation);
      }
      if (operation.phase === 'sample-uncertain') {
        if (await fingerprint(cart) !== operation.expectedSamples) throw uncertain('SAMPLE_RESULT_UNCERTAIN');
        return finish(operation, cart, [...new Set([...check(cart, operation.sampleTarget),
          ...reviewedTotalMismatches(operation, cart, 'expectedBagTotal')])]);
      }
      if (operation.phase !== 'paid-confirmed') throw uncertain('INVALID_RECOVERY_PHASE');
      if (await fingerprint(cart) !== operation.expectedPaid) throw uncertain('CART_CHANGED_AFTER_PAID_ADD');
      if (!operation.unitPrices || cart.items.some(line => plain(line) && Object.hasOwn(operation.unitPrices, variant(line)) &&
          (line.original_price ?? line.price) !== operation.unitPrices[variant(line)])) return finish(operation, cart, ['MERCHANDISE_PRICE_CHANGED']);
      if (!campaign.active(now())) return finish(operation, cart, ['CAMPAIGN_EXPIRED']);
      const paidMismatches = [...new Set([...check(cart, qty(cart.items.filter(owned))),
        ...reviewedTotalMismatches(operation, cart, 'expectedPaidTotal')])];
      if (paidMismatches.length) return finish(operation, cart, paidMismatches);
      const ownLines = cart.items.filter(owned);
      const otherCount = qty(samples(cart).filter(line => !owned(line)));
      const cap = campaign.evaluate(cart, now()).sampleCap;
      if (otherCount > cap || ownLines.length > 1) return finish(operation, cart, ['SAMPLE_OWNERSHIP_REVIEW']);
      const target = Math.min(cap - otherCount, qty(ownLines) + operation.disclosedSamples);
      if (qty(ownLines) > target) return finish(operation, cart, ['SAMPLE_QUANTITY_REVIEW']);
      if (target === qty(ownLines)) return finish(operation, cart, check(cart, target));
      if (!allowSampleWrite) return finish(operation, cart, ['SAMPLE_CONFIRMATION_REQUIRED']);
      const next = clone(cart);
      const existing = next.items.find(owned);
      if (existing) existing.quantity = target;
      else next.items.push({ variant_id: sampleId, quantity: target, properties });
      operation.phase = 'sample-uncertain';
      operation.sampleTarget = target;
      operation.expectedSamples = await fingerprint(next);
      coordinator.save(operation);
      if (!activeWrite(guard)) {
        operation.phase = 'paid-confirmed';
        delete operation.sampleTarget;
        delete operation.expectedSamples;
        coordinator.save(operation);
        return finish(operation, cart, ['SCOPE_EXPIRED']);
      }
      try {
        if (ownLines.length) await io.change({ id: ownLines[0].key, quantity: target });
        else await io.add([{ id: sampleId, quantity: target, properties }]);
      } catch (_) {}
      return continueOperation(operation, await read());
    }
    async function reduceWithinLock(cart, guard = () => true) {
        if (!activeWrite(guard)) return result(cart, ['SCOPE_EXPIRED'], false);
        const cap = campaign.evaluate(cart, now()).sampleCap;
        const ownLines = cart.items.filter(owned), otherCount = qty(samples(cart).filter(line => !owned(line)));
        if (ownLines.some(line => typeof line.key !== 'string' || !line.key)) return result(cart, ['INVALID_CART_RESPONSE'], false);
        if (ownLines.length > 1) return result(cart, ['SAMPLE_OWNERSHIP_REVIEW'], false);
        const target = Math.min(qty(ownLines), Math.max(0, cap - otherCount));
        if (!ownLines.length || target === qty(ownLines)) return result(cart, check(cart, target), false);
        const next = clone(cart); next.items.find(owned).quantity = target;
        const operation = { version: 2, kind: 'reduce', operationId: uuid(), cartIdentity: await identity(cart), campaignId: config.id,
          revision: config.revision, phase: 'sample-uncertain', sampleTarget: target, expectedSamples: await fingerprint(next), createdAt: now() };
        coordinator.save(operation);
        if (!activeWrite(guard)) {
          coordinator.clear(operation.operationId);
          return result(cart, ['SCOPE_EXPIRED'], false);
        }
        try { await io.change({ id: ownLines[0].key, quantity: target }); } catch (_) {}
        return continueOperation(operation, await read());
    }
    const paidLineSignature = line => JSON.stringify([variant(line), canonical(line.properties || {}),
      String(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || '')]);
    const decreaseFailure = (code, message, cart, partialSamplesAdjusted = false, isUncertain = false) => ({
      ok: false, code, message, cart, partialSamplesAdjusted, uncertain: isUncertain
    });
    async function decreaseWithinLock({ before, lineKey, quantity, guard, writePaid }) {
      enabledCheck();
      if (!coordinator.crossTab || typeof guard !== 'function' || typeof writePaid !== 'function')
        return decreaseFailure('safe_adjustment_unavailable', 'Could not safely adjust your sample gifts. Review your bag before reducing bottles.', before);
      const checkGuard = () => {
        const status = guard();
        if (status === false || (status && typeof status.then === 'function')) throw new Error('SCOPE_EXPIRED');
      };
      try { checkGuard(); } catch (_) {
        return decreaseFailure('safe_adjustment_unavailable', 'Could not safely adjust your sample gifts. Review your bag before reducing bottles.', before);
      }
      if (!before || !Array.isArray(before.items) || before.currency !== config.currency ||
          !integer(quantity) || typeof lineKey !== 'string' || !lineKey ||
          !integer(before.total_price) || before.items.some(line => !line || !integer(line.quantity) ||
            !integer(line.line_price) || !integer(line.final_line_price) || typeof line.key !== 'string' || !line.key) ||
          moneyState(before).mismatches.length) {
        return decreaseFailure('cart_review_required', 'Review your bag before reducing bottles.', before);
      }
      const matches = before.items.filter(line => line.key === lineKey);
      if (matches.length !== 1 || !campaign.qualifies(matches[0]) || quantity >= matches[0].quantity)
        return decreaseFailure('paid_line_changed', 'The bottle quantity changed. Review your bag and try again.', before);
      const original = matches[0], originalSignature = paidLineSignature(original);
      const initialIdentity = await identity(before);
      const projectedCart = clone(before);
      const projectedLine = projectedCart.items.find(line => line.key === lineKey);
      projectedLine.quantity = quantity;
      if (!quantity) projectedCart.items = projectedCart.items.filter(line => line !== projectedLine);
      let projectedCap;
      try { projectedCap = campaign.evaluate(projectedCart, now()).sampleCap; }
      catch (_) { return decreaseFailure('cart_review_required', 'Review your bag before reducing bottles.', before); }
      const initialOwned = qty(before.items.filter(owned));
      const unowned = qty(samples(before).filter(line => !owned(line)));
      const totalSamples = initialOwned + unowned;
      if (![initialOwned, unowned, totalSamples].every(integer))
        return decreaseFailure('cart_review_required', 'Review your bag before reducing bottles.', before);
      if (unowned > projectedCap)
        return decreaseFailure('unowned_samples_block_decrease', 'Other sample gifts exceed the new bottle limit. Remove them before reducing bottles.', before);
      if (validateExistingOffer && check(before, initialOwned).length)
        return decreaseFailure('cart_review_required', 'The bag amounts or gifts changed. Review your bag before reducing bottles.', before);
      if (totalSamples <= projectedCap) {
        try { checkGuard(); } catch (_) {
          return decreaseFailure('safe_adjustment_unavailable', 'Could not safely adjust your sample gifts. Review your bag before reducing bottles.', before);
        }
        return writePaid(lineKey, quantity);
      }
      const ownedTarget = projectedCap - unowned;
      if (initialOwned < totalSamples - projectedCap)
        return decreaseFailure('sample_ownership_review', 'Review your sample gifts before reducing bottles.', before);
      const operation = { version: 2, kind: 'paid-decrease', operationId: uuid(),
        cartIdentity: initialIdentity, campaignId: config.id, revision: config.revision,
        phase: 'sample-confirmed', paidVariantId: variant(original), paidQuantityBefore: original.quantity,
        paidQuantityRequested: quantity, paidSignature: await hash(originalSignature), projectedCap,
        unownedSamples: unowned, createdAt: now() };
      let cart = before, partial = false;
      const fail = (code, message, isUncertain = true) => decreaseFailure(code, message, cart, partial, isUncertain);
      const verified = async (observed, expected) =>
        await identity(observed) === initialIdentity && await fingerprint(observed) === expected &&
        qty(samples(observed).filter(line => !owned(line))) === unowned &&
        !moneyState(observed).mismatches.length &&
        (!validateExistingOffer || !check(observed, qty(observed.items.filter(owned))).length);
      const readActual = async () => { try { return await read(); } catch (_) { return null; } };
      const findPaid = observed => {
        const keyed = observed.items.filter(line => line.key === lineKey && paidLineSignature(line) === originalSignature);
        if (keyed.length === 1) return keyed[0];
        const candidates = observed.items.filter(line => paidLineSignature(line) === originalSignature && line.quantity === original.quantity);
        return candidates.length === 1 ? candidates[0] : null;
      };
      // This journal is written before every POST. A missing or ambiguous response
      // leaves it pending so neither this operation nor the quantity queue replays it.
      while (qty(cart.items.filter(owned)) > ownedTarget) {
        try { checkGuard(); } catch (_) { return fail('scope_expired', partial
          ? 'Sample gifts changed, but the bottle change was not sent. Review your bag.'
          : 'The offer window ended before any change. Review your bag.'); }
        const lines = cart.items.filter(owned).sort((a, b) => a.key.localeCompare(b.key));
        if (!lines.length || !findPaid(cart)) return fail('cart_review_required', 'Review your bag before changing bottles.');
        const ownCount = qty(lines), line = lines[0], remove = Math.min(line.quantity, ownCount - ownedTarget);
        const next = clone(cart), nextLine = next.items.find(item => item.key === line.key);
        nextLine.quantity -= remove;
        if (!nextLine.quantity) next.items = next.items.filter(item => item !== nextLine);
        operation.phase = 'sample-uncertain';
        operation.expectedBefore = await fingerprint(cart);
        operation.expectedAfter = await fingerprint(next);
        operation.ownedSamplesBefore = ownCount;
        operation.ownedSamplesAfter = ownCount - remove;
        try { coordinator.save(operation); }
        catch (_) { return fail('journal_unavailable', partial
          ? 'Sample gifts changed, but the bottle change was not sent. Review your bag.'
          : 'Could not safely adjust your sample gifts. Review your bag before reducing bottles.'); }
        try { checkGuard(); } catch (_) {
          return fail('scope_expired', 'The offer window ended before the next cart request. Review your bag.');
        }
        let sampleError = null;
        try { await io.change({ id: line.key, quantity: line.quantity - remove }); }
        catch (error) { sampleError = error; }
        const observed = await readActual();
        if (!observed) return fail('sample_result_uncertain', 'Could not confirm your sample gifts. Check your bag before trying again.');
        cart = observed;
        if (sampleError?.rejected && await verified(cart, operation.expectedBefore)) {
          try { coordinator.clear(operation.operationId); }
          catch (_) { return fail('journal_unavailable', 'Review your bag before another edit.'); }
          return fail('sample_adjustment_refused', partial
            ? 'Some sample gifts were reduced, but the bottle change was not applied. Review your bag.'
            : 'Sample gifts could not be adjusted. Your bottle quantity was not changed.', false);
        }
        if (sampleError || !await verified(cart, operation.expectedAfter) ||
            qty(cart.items.filter(owned)) !== operation.ownedSamplesAfter || !findPaid(cart))
          return fail('sample_result_uncertain', 'Could not confirm your sample gifts. Check your bag before trying again.');
        partial = true;
        operation.phase = 'sample-confirmed';
        operation.expectedBefore = await fingerprint(cart);
        delete operation.expectedAfter;
        try { coordinator.save(operation); }
        catch (_) { return fail('journal_unavailable', 'Sample gifts changed, but the bottle change was not sent. Review your bag.'); }
      }
      try { checkGuard(); } catch (_) {
        return fail('scope_expired', 'Sample gifts changed, but the bottle change was not sent. Review your bag.');
      }
      const paid = findPaid(cart);
      if (!paid) return fail('paid_line_changed', 'Sample gifts changed, but the bottle line could not be identified. Review your bag.');
      const next = clone(cart), nextPaid = next.items.find(line => line.key === paid.key);
      nextPaid.quantity = quantity;
      if (!quantity) next.items = next.items.filter(line => line !== nextPaid);
      operation.phase = 'paid-uncertain';
      operation.expectedBefore = await fingerprint(cart);
      operation.expectedAfter = await fingerprint(next);
      try { coordinator.save(operation); }
      catch (_) { return fail('journal_unavailable', 'Sample gifts changed, but the bottle change was not sent. Review your bag.'); }
      try { checkGuard(); } catch (_) {
        return fail('scope_expired', 'Sample gifts changed, but the bottle change was not sent. Review your bag.');
      }
      let paidResult;
      try { paidResult = await writePaid(paid.key, quantity); }
      catch (_) { paidResult = null; }
      const observed = await readActual();
      if (!observed) return fail('paid_result_uncertain', 'Sample gifts changed, but the bottle result could not be confirmed. Check your bag.');
      cart = observed;
      if (paidResult && !paidResult.ok && paidResult.status >= 400 && paidResult.status < 500 &&
          await verified(cart, operation.expectedBefore)) {
        operation.phase = 'paid-refused';
        try { coordinator.save(operation); coordinator.clear(operation.operationId); }
        catch (_) { return fail('journal_unavailable', 'Sample gifts were reduced; review your bag before another edit.'); }
        return fail('paid_decrease_refused', 'Sample gifts were reduced, but the bottle quantity was not changed. Review your bag.', false);
      }
      if (!paidResult?.ok || !await verified(cart, operation.expectedAfter) ||
          campaign.evaluate(cart, now()).eligibleCount !== campaign.evaluate(projectedCart, now()).eligibleCount ||
          qty(samples(cart)) > projectedCap) {
        return fail('paid_result_uncertain', 'Sample gifts changed; the bottle result is uncertain. Check your bag before another edit.');
      }
      operation.phase = 'paid-confirmed';
      try { coordinator.save(operation); coordinator.clear(operation.operationId); }
      catch (_) { return fail('journal_unavailable', 'The bottle change was applied, but the bag still needs review.'); }
      return { ok: true, status: paidResult.status, action: quantity === 0 ? 'remove' : 'change',
        cart, partialSamplesAdjusted: partial, uncertain: false };
    }
    return Object.freeze({ version: 2, enabled,
      decreaseWithinLock,
      async prepare({ lines, currency, unitPrices, includeSamples = true }) {
        enabledCheck(); linesCheck(lines);
        if (currency !== config.currency || !campaign.active(now())) throw new Error('CAMPAIGN_UNAVAILABLE');
        return coordinator.run(async () => review(await read(), lines, unitPrices, includeSamples), { requireCrossTab: true });
      },
      async transfer({ lines, currency, consent, guard = () => true }) {
        enabledCheck(); linesCheck(lines);
        return coordinator.run(async () => {
          if (!campaign.active(now()) || currency !== config.currency) throw new Error('CAMPAIGN_UNAVAILABLE');
          const before = await read(), current = await review(before, lines, consent?.unitPrices, consent?.includeSamples);
          if (JSON.stringify(current) !== JSON.stringify(consent)) throw new Error('REVIEW_CHANGED_CART');
          requireActiveWrite(guard);
          const expectedPaidTotal = current.estimatedBagTotal - current.additionalSampleCharge;
          if (!integer(expectedPaidTotal)) throw uncertain('FULL_BAG_ESTIMATE_UNAVAILABLE');
          const operation = { version: 2, kind: 'transfer', operationId: uuid(), cartIdentity: current.cartIdentity, revision: config.revision,
            campaignId: config.id, phase: 'paid-uncertain', expectedPaid: await fingerprint(projected(before, lines)),
            expectedPaidTotal,
            expectedBagTotal: current.estimatedBagTotal,
            disclosedSamples: current.sampleQuantityToAdd, unitPrices: current.unitPrices, createdAt: now() };
          coordinator.save(operation);
          if (!activeWrite(guard)) {
            coordinator.clear(operation.operationId);
            throw new Error('SCOPE_EXPIRED');
          }
          let rejection;
          try { await io.add(lines.map(({ id, quantity }) => ({ id: String(id), quantity }))); } catch (error) { rejection = error; }
          const cart = await read();
          if (rejection?.rejected && await identity(cart) === current.cartIdentity && await fingerprint(cart) === current.cartFingerprint) {
            coordinator.clear(operation.operationId); throw rejection;
          }
          return { ...await continueOperation(operation, cart, true, guard), before };
        }, { requireCrossTab: true });
      },
      async recover() {
        enabledCheck();
        return coordinator.run(async () => {
          const operation = coordinator.pending();
          if (!operation) return { mode: 'cart-observed', confirmed: false, offersReconciled: false, cart: await read(), mismatches: [] };
          return continueOperation(operation, await read(), false);
        }, { recovery: true, requireCrossTab: true });
      },
      async reviewRecovery() {
        enabledCheck();
        return coordinator.run(async () => {
          const operation = coordinator.pending();
          const cart = await read();
          const mismatches = check(cart, qty(cart.items.filter(owned)));
          return { cart, mismatches, snapshot: { operation: operation ? JSON.stringify(operation) : null,
            campaignId: config.id, revision: config.revision, cartIdentity: await identity(cart),
            cartReviewFingerprint: await reviewFingerprint(cart), mismatches } };
        }, { recovery: true, requireCrossTab: true });
      },
      async resolveReviewed(snapshot) {
        enabledCheck();
        return coordinator.run(async () => {
          const operation = coordinator.pending(), cart = await read();
          const mismatches = check(cart, qty(cart.items.filter(owned)));
          const current = { operation: operation ? JSON.stringify(operation) : null,
            campaignId: config.id, revision: config.revision, cartIdentity: await identity(cart),
            cartReviewFingerprint: await reviewFingerprint(cart), mismatches };
          if (!snapshot || JSON.stringify(current) !== JSON.stringify(snapshot)) throw uncertain('REVIEW_CHANGED_CART');
          if (operation && (operation.revision !== config.revision || operation.campaignId !== config.id || operation.cartIdentity !== current.cartIdentity)) throw uncertain('CART_OR_CAMPAIGN_CHANGED');
          if (operation) coordinator.clear(operation.operationId);
          return { ...result(cart, mismatches, false), mode: 'cart-review-resolved', resolved: true };
        }, { recovery: true, requireCrossTab: true });
      },
      async reduceAfterEdit() {
        enabledCheck();
        return coordinator.run(async () => reduceWithinLock(await read()), { requireCrossTab: true });
      },
      connect(guard = () => true) {
        enabledCheck();
        if (!coordinator.crossTab) throw uncertain('CROSS_TAB_LOCK_UNAVAILABLE');
        return coordinator.registerReconciler(cart => reduceWithinLock(cart, guard));
      }
    });
  }
  const api = { version: 2, create, createShopifyIO, configuration: Object.freeze({ enabled: false }) };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.TheManBundleCart = api;
})(typeof window === 'object' ? window : globalThis);
